/* eslint-disable react/require-default-props */
/* eslint-disable jam3/no-sanitizer-with-danger */
/* eslint-disable react/no-danger */
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import '@/lib/jquery';
import { cloneDeep } from 'lodash';

import FrameContentView from './FrameContentView';
import { FrameTooltipsBar, FrameTooltipsPopovers } from './FrameTooltips';
import { dragdropFix } from './dragdropFix';
import { installTouchPunchReady } from './prepareTouchPunch';
import {
  getLocalstorage,
  hasVocamData,
  resolveAssetUrl,
  useIsMobileScreenSize,
} from '@/lib/util';
import { Modal, Alert } from 'elmo-elements';

import { FrameDataType } from '@/modules/types/frame';
import { currentFrameDataSelector } from '@/modules/selectors/frame';

import { useFrameStyle } from '@/modules/hooks/useFrameStyle';
import FrameContentStyleIsolator from '@/modules/utils/FrameContentStyleIsolator';
import {
  AudioPlayerStateType,
  actions as AudioPlayerActions,
} from '@/components/Player/modules/reducers/audioPlayer';
import {
  actions as VideoPlayerActions,
  VideoPlayerStateType,
} from '@/components/Player/modules/reducers/videoPlayer';

import {
  contentTooltip,
  getVideoOptions,
  setTipBar,
  updatePlayPauseIcon,
} from './util';
import HowlerAudioPlayer from '@/components/AudioPlayer/Howler';
import VideoPlayer from '@/components/VideoPlayer';
import { ErrorBoundary } from 'react-error-boundary';
import { errorHandler } from '@/lib/Error';
import { Fallback } from '@/lib/Error/fallback';
import { ElearningPlayerStateType } from '@/components/Player/modules/reducers/elearningPlayer';
import SoundVolumeBar from '@/components/SoundVolumeBar';
import VideoPlayerHls from '@/components/VideoPlayerHls';
import { MuteType } from '@/modules/types/elearning/muteType.enum';

export const FrameContentProcessor = ({
  currentFrame,
  isMobileView,
}: {
  currentFrame: FrameDataType;
  isMobileView?: boolean;
}): ReactElement => {
  const dispatch = useDispatch();
  const tipBarRef = useRef<HTMLDivElement>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [currentVideoOption, setCurrentVideoOption] = useState<
    undefined | any
  >();

  const elearningBehaviourSettings = useSelector(
    (state: { elearningPlayer: ElearningPlayerStateType }) =>
      state.elearningPlayer.elearningBehaviourSettings,
  );

  const {
    audioPlayer,
    elearningPlayer: {
      content,
      handlers: { startAssessment },
    },
  }: {
    audioPlayer: AudioPlayerStateType;
    elearningPlayer: ElearningPlayerStateType;
  } = useSelector(
    (state: {
      audioPlayer: AudioPlayerStateType;
      elearningPlayer: ElearningPlayerStateType;
    }) => state,
  );
  const { vocamVideoLink } = useSelector(
    (state: { videoPlayer: VideoPlayerStateType }) => state.videoPlayer,
  );
  const { quizGetAttemptData } = content;

  useFrameStyle(currentFrame, isMobileView);

  const setAssessmentButton = () => {
    const assessmentBtn = document.getElementById('do-assessment');
    if (assessmentBtn) {
      assessmentBtn.onclick = (event) => {
        startAssessment(quizGetAttemptData);
        event.preventDefault();

        dispatch({
          type: AudioPlayerActions.endAudio.type,
        });
      };
    }
  };

  const handleSoundPlayPause = (source: string) => {
    dispatch({
      type: AudioPlayerActions.playPauseAudio.type,
      payload: { source, entity: 'frameContent' },
    });

    return false;
  };

  const handleVideoPlayer = (options: any, loaded: boolean): void => {
    dispatch({
      type: VideoPlayerActions.getVideoOptions.type,
      payload: { options, loaded },
    });
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const videoEmbeds: any = document.querySelectorAll(
      '[name="custom_media_embed"]',
    );

    if (videoEmbeds.length) {
      let videoEmbedOptions: any = [];
      videoEmbedOptions = getVideoOptions(videoEmbeds, currentFrame.id);

      handleVideoPlayer([...videoEmbedOptions], true);
    }
    return () => {
      handleVideoPlayer([], false);
    };
  }, [currentFrame]);

  const setVideoPopupContent = () => {
    const videoPopups: any = document.querySelectorAll(
      '[name="custom_media_popup"]',
    );
    if (videoPopups.length) {
      let videoPopupOptions: any = [];
      videoPopupOptions = getVideoOptions(
        videoPopups,
        currentFrame.id,
        'popup',
      );
      videoPopups.forEach((elem: HTMLDivElement, index: number) => {
        elem.onclick = (e: any) => {
          e.preventDefault();
          setModalOpen(true);
          setCurrentVideoOption(videoPopupOptions[index]);
        };
      });
    }
    return false;
  };

  useEffect(() => {
    // Stops any audio playing when changing frames.
    dispatch({
      type: AudioPlayerActions.endAudio.type,
    });

    const customMedia = document.querySelectorAll("div[name='custom_media']");
    if (customMedia.length > 0) {
      customMedia.forEach((media) => {
        const mediaSource = media.getAttribute('src');
        media.addEventListener('click', () => {
          handleSoundPlayPause(`${mediaSource}`);
          updatePlayPauseIcon(media);
        });
      });
    }
  }, [currentFrame]);

  useEffect(() => {
    if (
      (!audioPlayer.loaded && audioPlayer.source !== '') ||
      !audioPlayer.playing
    ) {
      const customMedia = document.querySelectorAll("div[name='custom_media']");
      customMedia.forEach((customMediaElement) => {
        customMediaElement.classList.remove('play');
      });
    }
  }, [audioPlayer.loaded, audioPlayer.playing]);

  const { isMobile } = useIsMobileScreenSize();

  const handlerFrameContentReady = () => {
    installTouchPunchReady();
    FrameContentStyleIsolator();
    setAssessmentButton();
    setTipBar(tipBarRef, isMobile);
    setVideoPopupContent();

    const setupDragDrop = () => {
      dragdropFix();
      window.$('.drag:not(.ui-draggable)').draggable();
      window.$('.drop:not(.ui-droppable)').droppable();
    };
    if (window.touchPunchReady) {
      window.touchPunchReady(setupDragDrop);
    } else {
      setupDragDrop();
    }
  };

  /**
   * The HTML snitising below is disabled temporarily. In order to make the user content work.
   */
  // const purifiedHTML = (sanitizeHTML(
  //   contentTooltip(currentFrame.content),
  //   CONFIG_SANITIZE_HTML,
  // ) as unknown) as string;

  const currentFrameContent =
    isMobile && currentFrame.mobileContent
      ? currentFrame.mobileContent
      : currentFrame.content;
  const purifiedHTML = contentTooltip(currentFrameContent);

  const hasAllVocamData = hasVocamData(currentFrame);

  return (
    <>
      <div className="module-viewer-style">
        {audioPlayer.source && (
          <SoundVolumeBar
            showMute={currentFrame.frameMetadata.mute !== MuteType.HIDDEN}
            isEnabled={currentFrame.frameMetadata.mute === MuteType.YES}
          />
        )}
        <HowlerAudioPlayer />
        <Modal
          id="ModalDialog"
          type="large"
          isOpened={modalOpen}
          closeModal={() => {
            setModalOpen(false);
            setCurrentVideoOption(null);
          }}
          closeLabel="Close"
          title={currentVideoOption?.label}
        >
          {currentVideoOption && (
            <VideoPlayerHls
              useHls={hasAllVocamData}
              videoLink={currentVideoOption.sources[0].src}
              key={currentVideoOption.styleId}
              option={currentVideoOption}
            />
          )}
        </Modal>
        <FrameTooltipsPopovers
          elements={currentFrame.elements}
          isMobileView={isMobileView}
        />
        <div className="canvas-content" id="FrameContent">
          <ErrorBoundary onError={errorHandler} FallbackComponent={Fallback}>
            {currentFrame.isVideoFrame &&
              (!!currentFrame?.bgVideo ||
                (hasAllVocamData &&
                  elearningBehaviourSettings.videoLibraryEnabled)) && (
                <VideoPlayerHls
                  videoLink={
                    hasAllVocamData && vocamVideoLink
                      ? vocamVideoLink
                      : resolveAssetUrl({
                          assetBasePath: currentFrame.assetBasePath,
                          tenantId: currentFrame.tenantId,
                          entity: 'elearnings',
                          entityId: currentFrame.elearningId,
                          fileType: 'videos',
                          fileName: `${currentFrame.bgVideo}`,
                        })
                  }
                  useHls={hasAllVocamData}
                />
              )}
            {currentFrame.isVideoFrame &&
              !currentFrame?.bgVideo &&
              hasAllVocamData &&
              !elearningBehaviourSettings.libraryEnabled && (
                <div className="video-library-message">
                  <p>
                    Your Video Library license has been disabled. Please,
                    contact your Account Manager to re-enable this
                  </p>
                </div>
              )}
            {!currentFrame.isVideoFrame && !hasAllVocamData && (
              <>
                <FrameContentView
                  currentFrame={currentFrame}
                  content={purifiedHTML}
                  onFrameContentReady={handlerFrameContentReady}
                />
                <VideoPlayer useHls={hasAllVocamData} />
              </>
            )}
          </ErrorBoundary>
        </div>
      </div>

      <FrameTooltipsBar
        elements={currentFrame.elements}
        tipBarRef={tipBarRef}
      />
    </>
  );
};

const FrameContent = ({
  isMobileView,
}: {
  isMobileView?: boolean;
}): ReactElement => {
  const {
    isPreview,
    content: { elearning, frames, currentFrameId },
  } = useSelector(
    (state: { elearningPlayer: ElearningPlayerStateType }) =>
      state.elearningPlayer,
  );

  const elearningId = elearning?.id;

  const [frameData, setFrameData] = useState<FrameDataType[] | undefined>(
    frames,
  );

  function overridePreviewContent(
    sourceFrame: FrameDataType[] | undefined,
    previewContent: string,
  ) {
    let updatedFrameData: FrameDataType[] | undefined = cloneDeep(sourceFrame);
    if (updatedFrameData && updatedFrameData) {
      if (currentFrameId) {
        // eslint-disable-next-line no-use-before-define
        updatedFrameData = updatedFrameData.map((item) =>
          item.id === currentFrameId
            ? { ...item, content: previewContent }
            : item,
        );
      } else {
        // eslint-disable-next-line no-use-before-define
        updatedFrameData[0].content = previewContent;
      }
      return updatedFrameData;
    }
    return undefined;
  }

  useEffect(() => {
    const newPreviewContent: {
      elearningId: string;
      frameId: string;
      content: string;
      mobileContent: string;
    } = getLocalstorage('PreviewContent', 'object');

    if (elearning) {
      if (
        isPreview &&
        elearning.id === newPreviewContent.elearningId &&
        currentFrameId === newPreviewContent.frameId
      ) {
        const content =
          (isMobileView && newPreviewContent.mobileContent) ||
          newPreviewContent.content;

        setFrameData(overridePreviewContent(frames, content));
      } else {
        setFrameData(frames);
      }
    }
  }, [frames]);

  const currentFrame = currentFrameDataSelector({
    elearningData: elearning,
    frameData,
    currentFrameId,
  });

  const onPreviewChange = (e: StorageEvent) => {
    const currentUrl = new URL(window.location.href);
    const currentUrlFrameId = currentUrl.pathname.split('/')[3];
    if (e.key === 'PreviewContent') {
      const newPreviewContent: {
        elearningId: string;
        frameId: string;
        content: string;
        mobileContent: string;
      } = getLocalstorage('PreviewContent', 'object');

      if (elearningId !== newPreviewContent.elearningId) {
        window.location.href = `${currentUrl.origin}/preview/${
          newPreviewContent.elearningId
        }/${
          newPreviewContent.frameId !== undefined
            ? newPreviewContent.frameId
            : ''
        }`;
      } else if (currentUrlFrameId !== newPreviewContent.frameId) {
        window.location.href = `${currentUrl.origin}/preview/${
          newPreviewContent.elearningId
        }/${
          newPreviewContent.frameId !== undefined
            ? newPreviewContent.frameId
            : ''
        }`;
      }
      const content =
        (isMobileView && newPreviewContent.mobileContent) ||
        newPreviewContent.content;
      setFrameData(overridePreviewContent(frames, content));
    }
  };

  useEffect(() => {
    if (frames && isPreview) {
      window.removeEventListener('storage', onPreviewChange);
      // need to re-attach onPreviewChange to event so it uses the latest values
      window.addEventListener('storage', onPreviewChange);
    }
  }, [frames]);

  useEffect(
    () => () => {
      window.removeEventListener('storage', onPreviewChange);
    },
    [],
  );

  if (currentFrame) {
    return (
      <FrameContentProcessor
        currentFrame={currentFrame}
        isMobileView={isMobileView}
      />
    );
  }

  return <Alert message="No Frame data" type="warning" isCloseable={false} />;
};

export default FrameContent;
